import React, { useState } from 'react';
import ItemEditor from './ItemEditor';
import SwipeToDelete from './SwipeToDelete';
import { fmtMoney } from '../lib/format';

// An estimate or an invoice, written on a full screen the way Joist writes one
// (built 2026-08-18 from Julian's Edit Estimate screenshot): Cancel / title /
// Done across the top, the client in their own section, then the lines — each
// one its NAME, the paragraph that says what it buys, its photos and its price
// — and Add Line Item at the bottom.
//
// It replaced a bottom sheet whose line was a ~140px description box next to a
// price box. That is fine for "DRYWALL 850" and useless for the estimates
// Julian actually sends, which read:
//
//   DRYWALL                                              $850.00
//   This includes labor and supplies to:
//   - Sand filing completely smooth
//   - Apply orange peel texture on the ceiling to match
//   - Have ready to paint
//
// A customer argues with a number. A customer agrees to a scope. The whole
// point of the line having its own screen is that there is room to write the
// second one — so tapping a line opens ItemEditor, the same Item Details
// screen the technician portal already uses, in its document flavour: no
// labour/materials question, and a price you can type outright.
//
// Both documents share this shell because they are the same act twice — the
// estimate is what the work will cost, the invoice is what it did.

export default function DocEditor({
  title,                 // 'New Estimate' | 'Edit Estimate' | 'New Invoice' | …
  clientName,
  clientSub,             // the line under the name: the job, or the address
  // Correcting who the document is for, from inside the document. Without it
  // the name at the top is the one thing on the sheet that cannot be fixed,
  // and a wrong email there is a bill that reaches nobody.
  onEditClient,
  header,                // the fields above the lines: job + service type, or project + number + date
  lines, onChangeLines,  // [{ label, description, photos, qty, rate, cost }]
  // Money that comes off or goes on after the lines are added — a discount,
  // the tax. [{ key, label, value, onChange, sign }]. The estimate passes
  // none, so it shows one Total and nothing else; an invoice passes both and
  // gets the Subtotal / Discount / Tax / Total block a bill is read with.
  adjustments = [],
  // A line under the Total for the person typing — what the customer will
  // actually be shown once the card fee is built in. Never printed.
  totalNote,
  notes, onChangeNotes,  // estimate only — notes for the homeowner
  notesLabel = 'Notes for Homeowner',
  banner,                // anything that has to be said before he starts typing
  doneLabel, onDone, saving,
  // Deleting the whole document, from inside it. Only passed when there is
  // something to delete — a document being written for the first time has
  // Cancel for that, and a red button under a blank form is a trap.
  onDelete, deleteLabel,
  onCancel,
  backLabel = 'Estimate',
}) {
  // Which line is open on its own screen. Null is the document itself.
  const [editing, setEditing] = useState(null);
  // Which row is asking "are you sure". Index, not the line itself, because
  // the list is rebuilt on every keystroke in the editor above it.
  const [pendingDelete, setPendingDelete] = useState(null);

  const subtotal = lines.reduce((t, l) => t + (parseFloat(l.cost) || 0), 0);
  const total = adjustments.reduce(
    (t, a) => t + (a.sign || 1) * (parseFloat(a.value) || 0),
    subtotal,
  );

  const setLine = (i, patch) => {
    const next = [...lines];
    next[i] = { ...next[i], ...patch };
    onChangeLines(next);
  };

  // A new line opens straight onto its own screen rather than appearing as a
  // blank row to be found and tapped — the tap that added it said what he
  // wanted next.
  const addLine = () => {
    onChangeLines([...lines, { label: '', description: '', photos: [], files: [], cost: '', qty: '', rate: '' }]);
    setEditing(lines.length);
  };

  // ItemEditor speaks in items; the document speaks in rows. Translated here
  // rather than in the editor, so the editor stays the one screen both portals
  // share.
  const asItem = (l) => ({
    name: l.label || '',
    description: l.description || '',
    photos: l.photos || [],
    files: l.files || [],
    qty: l.qty ?? '',
    unitCost: l.rate ?? '',
    price: l.cost ?? '',
  });
  const fromItem = (it) => ({
    label: it.name,
    description: it.description,
    photos: it.photos || [],
    files: it.files || [],
    qty: it.qty,
    rate: it.unitCost,
    cost: it.price,
  });

  if (editing != null && lines[editing]) {
    return (
      <ItemEditor
        variant="doc"
        backLabel={backLabel}
        item={asItem(lines[editing])}
        index={editing}
        total={lines.length}
        onChange={(it) => setLine(editing, fromItem(it))}
        onDelete={() => {
          onChangeLines(lines.filter((_, i) => i !== editing));
          setEditing(null);
        }}
        onClose={() => setEditing(null)}
      />
    );
  }

  return (
    <>
      <div className="modal active" style={{ alignItems: 'stretch' }}>
        <div className="de">
          <div className="de-bar">
            <button className="de-bar-btn" onClick={onCancel}>Cancel</button>
            <span className="de-bar-title">{title}</span>
            <button className="de-bar-btn" disabled={saving} onClick={onDone}>{saving ? 'Saving…' : 'Done'}</button>
          </div>

          <div className="de-body">
            {banner}

            <div className="de-sec">Client</div>
            <div className="de-client">
              <div style={{ minWidth: 0, flex: 1 }}>
                <div className="de-client-name">{clientName}</div>
                {clientSub && <div className="de-client-sub">{clientSub}</div>}
              </div>
              {onEditClient && (
                <button type="button" className="de-mini" onClick={onEditClient}>Edit</button>
              )}
            </div>

            {header}

            <div className="de-sec de-sec-split">
              <span>Description</span>
              <span className="de-sec-total">{fmtMoney(total)}</span>
            </div>

            {lines.length === 0 && (
              <div className="de-empty">Nothing on this document yet. Add the first line below.</div>
            )}

            <div className="de-line-list">

            {/* ── TAKING A LINE OFF ────────────────────────────────────
                Deleting a line used to mean opening it and finding Delete
                at the bottom of its own screen, which is two screens away
                from the list you are looking at while you decide.

                Drag the row left, press Delete, and it asks once more. The
                second press is not ceremony: these rows are money on a
                document, the list is read on a phone with a thumb, and an
                undo that does not exist cannot be the safety net. */}
            {lines.map((l, i) => {
              const name = l.label?.trim() || 'Untitled line';

              // The confirm replaces the row rather than floating over it.
              // This whole editor is already inside a modal, and a dialog on
              // top of a dialog is the thing people dismiss without reading.
              if (pendingDelete === i) {
                return (
                  <div className="de-del" key={`del-${i}`}>
                    <div className="de-del-txt">
                      <b>Delete {name}?</b>
                      <span>Taking it off this document cannot be undone.</span>
                    </div>
                    <div className="de-del-acts">
                      <button type="button" className="de-del-keep" onClick={() => setPendingDelete(null)}>
                        Keep it
                      </button>
                      <button
                        type="button" className="de-del-go"
                        onClick={() => {
                          setPendingDelete(null);
                          onChangeLines(lines.filter((_, k) => k !== i));
                        }}
                      >
                        Delete
                      </button>
                    </div>
                  </div>
                );
              }

              return (
                <SwipeToDelete key={i} onDelete={() => setPendingDelete(i)}>
                  <button className="de-line" onClick={() => setEditing(i)}>
                    <div className="de-line-main">
                      <div className="de-line-head">
                        <span className="de-line-name">{name}</span>
                        {(l.photos || []).length > 0 && (
                          <span className="de-line-photos">{(l.photos || []).length} photo{(l.photos || []).length === 1 ? '' : 's'}</span>
                        )}
                      </div>
                      {l.description?.trim() && <div className="de-line-desc">{l.description}</div>}
                    </div>
                    <div className="de-line-right">
                      <span className="de-line-amt">{fmtMoney(parseFloat(l.cost) || 0)}</span>
                      <span className="de-line-chev">›</span>
                    </div>
                  </button>
                </SwipeToDelete>
              );
            })}

            </div>

            <button className="de-add" onClick={addLine}>+ Add Line Item</button>

            {/* One number when there is nothing to adjust — a Subtotal that
                equals the Total is a row that says nothing twice. */}
            {adjustments.length > 0 && (
              <div className="de-sum">
                <span>Subtotal</span>
                <span>{fmtMoney(subtotal)}</span>
              </div>
            )}
            {adjustments.map(a => (
              <div className="de-sum" key={a.key}>
                <span>{a.label}</span>
                <span className="de-sum-in">
                  {a.sign === -1 && (parseFloat(a.value) || 0) > 0 ? '−' : ''}$
                  <input
                    type="number" inputMode="decimal" min="0" step="0.01"
                    value={a.value} placeholder="0.00"
                    onChange={e => a.onChange(e.target.value)}
                  />
                </span>
              </div>
            ))}

            <div className="de-total">
              <span>Total</span>
              <span>{fmtMoney(total)}</span>
            </div>
            {totalNote}

            {onChangeNotes && (
              <>
                <div className="de-sec">{notesLabel}</div>
                <textarea
                  className="de-notes" rows={4} value={notes || ''}
                  placeholder="Any details, timelines, or conditions…"
                  onChange={e => onChangeNotes(e.target.value)}
                />
              </>
            )}

            <div className="de-foot">
              <button className="btn btn-primary btn-lg" disabled={saving} onClick={onDone}>
                {saving ? 'Saving…' : doneLabel}
              </button>
              {onDelete && (
                <button
                  className="btn btn-ghost btn-lg"
                  style={{ color: 'var(--red)', borderColor: 'var(--red)', marginTop: 10 }}
                  onClick={onDelete}
                >{deleteLabel || 'Delete'}</button>
              )}
            </div>
          </div>
        </div>
      </div>
      <div className="overlay active" onClick={onCancel}></div>
    </>
  );
}
